<template>
  <div ref="container" class="h-full w-full">
    <svg :width="width" :height="height">
      <path
        class="fill-none"
        :stroke-width="thickness"
        :d="path"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>
    </svg>
  </div>
</template>
<script lang="ts" setup>
  import { TransitionPresets, useElementBounding, useTransition } from '@vueuse/core'
  import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
  import { normalize } from '../../../utils/normalize'

  interface Props {
    wavelength?: number
    thickness?: number
    animateAmplitude?: boolean
    duration?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    wavelength: 40,
    thickness: 4,
    animateAmplitude: false,
    duration: 900,
  })

  const container = useTemplateRef('container')
  const { width, height } = useElementBounding(container)

  const amplitudeTarget = ref(props.animateAmplitude ? 0 : 1)
  const amplitudeOutput = useTransition(amplitudeTarget, {
    duration: props.duration,
    transition: TransitionPresets.easeInOutQuad,
    onFinished: () => {
      if (!props.animateAmplitude) return
      amplitudeTarget.value = amplitudeTarget.value >= 0.5 ? 0 : 1
    },
  })

  const path = computed(() => {
    let nextPath = ''

    if (width.value <= 0 || height.value <= 0) return nextPath

    const step = Math.max(width.value / 400, 1)
    const amplitudeFactor = props.animateAmplitude ? amplitudeOutput.value : 1
    const maxOffset = height.value / 2 - props.thickness / 2

    for (let pixelIndex = 0; pixelIndex < width.value - props.thickness; pixelIndex += step) {
      const factor = Math.sin(
        normalize(pixelIndex % props.wavelength, 0, props.wavelength) * Math.PI * 2 - Math.PI * 2,
      )

      nextPath += pixelIndex == 0 ? 'M ' : ' L '
      nextPath += `${pixelIndex + props.thickness / 2} ${height.value / 2 + maxOffset * factor * amplitudeFactor}`
    }

    return nextPath
  })

  const startAmplitudeLoop = () => {
    amplitudeTarget.value = 0
    requestAnimationFrame(() => {
      amplitudeTarget.value = 1
    })
  }

  watch(
    () => props.animateAmplitude,
    (shouldAnimate) => {
      if (shouldAnimate) {
        startAmplitudeLoop()
      } else {
        amplitudeTarget.value = 1
      }
    },
  )

  onMounted(() => {
    if (props.animateAmplitude) {
      startAmplitudeLoop()
    }
  })
</script>
